import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { FormattedMessage } from 'react-intl';

import { HpRespModuleContainerOld } from '../HpRespModuleContainerOld/HpRespModuleContainerOld';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';
import { HpRespHeroModuleItemOld } from './HpRespHeroModuleItemOld';

import { RenderIn, device } from '../../utils/RenderIn';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { trackHeroImpression, trackHeroItemClick } from './helpers/HeroTrackingOld';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { TABLET_ITEMS_PER_PAGE } from '../constants';

/**
 *  Removes items residue because we want to make sure that all of the carousel pages are full
 */
function removeItemsResidue({ totalItems, itemsPerPage }) {
    const residue = totalItems % itemsPerPage;

    if (residue !== 0) {
        return totalItems - residue;
    }

    return totalItems;
}

function HpRespHeroModuleOldComponent({ homepage }) {
    if (!homepage.heroModuleOld) {
        return null;
    }

    const { items, title } = homepage.heroModuleOld;

    return (
        <HpRespModuleContainerOld>
            <HpRespModuleContainerOld.Header>
                {title ? (
                    title
                ) : (
                    <FormattedMessage
                        id="homepage.modules.hero.old.title"
                        defaultMessage="Editor's Picks"
                    />
                )}
            </HpRespModuleContainerOld.Header>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {({ currentDevice }) => {
                    const itemsPerPage =
                        currentDevice === device.DESKTOP ? TABLET_ITEMS_PER_PAGE : 3;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackHeroImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <SharedInViewport>
                                    {({ inViewport }) => (
                                        <Carousel
                                            isAutoRun={inViewport}
                                            dataTn="hero-items-carousel"
                                            showDots
                                            hideArrows
                                            totalItems={removeItemsResidue({
                                                totalItems: items.length,
                                                itemsPerPage,
                                            })}
                                            itemsPerPage={itemsPerPage}
                                            step={itemsPerPage}
                                            renderItem={({ index, isVisible }) => (
                                                <HpRespHeroModuleItemOld
                                                    item={items[index]}
                                                    isVisible={isVisible}
                                                    onClick={() =>
                                                        trackHeroItemClick({
                                                            item: items[index],
                                                            index,
                                                        })
                                                    }
                                                />
                                            )}
                                            onIndexChange={handleIndexChange}
                                            renderDot={({ isCurrentDot } = {}) => (
                                                <SharedCarouselDot isActive={isCurrentDot} />
                                            )}
                                        />
                                    )}
                                </SharedInViewport>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>

            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 1.5;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackHeroImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handlePageChange }) => (
                                <Swiper itemsPerPage={itemsPerPage} onPageChange={handlePageChange}>
                                    {items.map((item, index) => (
                                        <HpRespHeroModuleItemOld
                                            key={item.cmsId}
                                            item={item}
                                            onClick={() => trackHeroItemClick({ item, index })}
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpRespModuleContainerOld>
    );
}

HpRespHeroModuleOldComponent.propTypes = {
    homepage: PropTypes.object.isRequired,
};

export const HpRespHeroModuleOld = createFragmentContainer(HpRespHeroModuleOldComponent, {
    homepage: graphql`
        fragment HpRespHeroModuleOld_homepage on Homepage {
            heroModuleOld: module(type: "hero") {
                ... on HeroModule {
                    title
                    items {
                        cmsId
                        link
                        imageUrl
                        title
                        contemporaryTrackingString
                    }
                }
            }
        }
    `,
});
